import { supabase } from './supabase';

type ApiErrorPayload = { error?: string; message?: string; code?: string };

const ACTIVE_ORGANIZATION_KEY = 'enerlectra.activeOrganizationId';

export function getActiveOrganizationId(): string | null {
  return typeof window === 'undefined' ? null : window.localStorage.getItem(ACTIVE_ORGANIZATION_KEY);
}

export function setActiveOrganizationId(organizationId: string): void {
  if (!organizationId.trim()) throw new Error('A valid organization is required.');
  window.localStorage.setItem(ACTIVE_ORGANIZATION_KEY, organizationId);
  window.dispatchEvent(new CustomEvent('enerlectra:organization-changed', { detail: organizationId }));
}

export function clearActiveOrganizationId(): void {
  window.localStorage.removeItem(ACTIVE_ORGANIZATION_KEY);
  window.dispatchEvent(new CustomEvent('enerlectra:organization-changed', { detail: null }));
}

function isOrganizationDiscoveryPath(path: string): boolean {
  return path === '/onboarding/organizations';
}

async function request<T>(path: string, method: 'GET' | 'POST' | 'PUT', body?: unknown): Promise<T> {
  const { data, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  const accessToken = data.session?.access_token;
  if (!accessToken) throw new Error('Your session has expired. Sign in again.');

  const organizationId = getActiveOrganizationId();
  if (!isOrganizationDiscoveryPath(path) && !organizationId) {
    throw new Error('Choose an organization before continuing. (ORGANIZATION_CONTEXT_REQUIRED)');
  }

  const requestId = crypto.randomUUID();
  const response = await fetch(`/api${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-Request-ID': requestId,
      'X-Correlation-ID': requestId,
      ...(organizationId ? { 'X-Organization-ID': organizationId } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    credentials: 'same-origin',
  });

  const responseText = await response.text();
  let payload: unknown;
  if (responseText) {
    try { payload = JSON.parse(responseText); } catch { payload = undefined; }
  }

  if (!response.ok) {
    const details = payload && typeof payload === 'object' ? payload as ApiErrorPayload : undefined;
    const message = details?.error || details?.message || `Request failed with status ${response.status}`;
    if (details?.code === 'MEMBERSHIP_NOT_FOUND' || details?.code === 'ORGANIZATION_INACTIVE') {
      clearActiveOrganizationId();
    }
    throw new Error(details?.code ? `${message} (${details.code})` : message);
  }
  if (payload === undefined) throw new Error('The API returned an empty or invalid JSON response.');
  return payload as T;
}

export function apiGet<T>(path: string): Promise<T> { return request<T>(path, 'GET'); }
export function apiPost<T>(path: string, body: unknown): Promise<T> { return request<T>(path, 'POST', body); }
export function apiPut<T>(path: string, body: unknown): Promise<T> { return request<T>(path, 'PUT', body); }
